import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ChevronDown, ChevronUp, Copy, Plus, RefreshCw, Save, Trash2, Users,
} from "lucide-react";

import { useStore } from "../context/StoreContext";
import { PLATFORMS } from "../lib/platforms";
import { fmtDateLong, fmtTime } from "../lib/format";
import { PlatformIcon } from "../components/branding";
import { ExportMenu, GhostBtn, INPUT, PrimaryBtn, Spinner } from "../components/ui";

const emptyBrief = () => ({ concept: "", format: "", hook: "", script: [], shots: [], caption: "" });

function hasContent(b) {
  if (!b) return false;
  return Boolean(
    b.concept || b.format || b.hook || b.caption || (b.script && b.script.length) || (b.shots && b.shots.length),
  );
}

function ugcToText(post, b) {
  const meta = PLATFORMS[post.platform] || { name: post.platform };
  const L = [];
  L.push(`${post.org || "UGC brief"} ${meta.name} UGC brief`);
  L.push(`${fmtDateLong(post.date)} at ${fmtTime(post.time)}`);
  L.push("");
  if (b.concept) L.push(`Concept: ${b.concept}`);
  if (b.format) L.push(`Format: ${b.format}`);
  if (b.hook) L.push(`Hook: ${b.hook}`);
  if ((b.script || []).length) {
    L.push("");
    L.push("Script:");
    b.script.forEach((s) => L.push(`- ${s}`));
  }
  if ((b.shots || []).length) {
    L.push("");
    L.push("Shot list:");
    b.shots.forEach((s) => L.push(`- ${s}`));
  }
  if (b.caption) {
    L.push("");
    L.push(`Caption: ${b.caption}`);
  }
  return L.join("\n").trim() + "\n";
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-faint mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function ListEditor({ label, addLabel, items, placeholder, onChange }) {
  const setItem = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () => onChange([...items, ""]);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <Field label={label}>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="mt-2.5 text-[12px] text-faint w-4 text-right shrink-0">{i + 1}</span>
            <textarea
              className={`${INPUT} py-1.5 text-[14px] leading-relaxed min-h-[42px] flex-1 min-w-0`}
              value={it}
              placeholder={placeholder}
              onChange={(e) => setItem(i, e.target.value)}
              rows={1}
            />
            <div className="flex items-center shrink-0 pt-0.5">
              <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up" className="w-7 h-7 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition disabled:opacity-30">
                <ChevronUp size={14} />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Move down" className="w-7 h-7 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition disabled:opacity-30">
                <ChevronDown size={14} />
              </button>
              <button onClick={() => remove(i)} title="Remove" className="w-7 h-7 rounded-lg flex items-center justify-center text-muted hover:bg-foreground/5 hover:text-foreground transition">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 ? <div className="text-[13px] text-faint">Nothing yet.</div> : null}
      </div>
      <button onClick={add} className="mt-2 text-[13px] text-muted hover:text-foreground transition flex items-center gap-1.5">
        <Plus size={14} /> {addLabel}
      </button>
    </Field>
  );
}

export default function UgcPage() {
  const { id } = useParams();
  const postId = Number(id);
  const navigate = useNavigate();
  const { posts, generateUgc, saveUgc, ugcId, exportItem, showToast } = useStore();

  const post = useMemo(() => posts.find((p) => p.id === postId), [posts, postId]);
  const [brief, setBrief] = useState(() => ({ ...emptyBrief(), ...(post && post.ugc ? post.ugc : {}) }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBrief({ ...emptyBrief(), ...(post && post.ugc ? post.ugc : {}) });
  }, [post && post.id, post && JSON.stringify(post && post.ugc)]);

  const busy = ugcId === postId;

  if (!post) {
    return (
      <div className="animate-fade-in max-w-lg mx-auto text-center py-20">
        <Users size={26} className="text-faint mx-auto mb-3" />
        <div className="text-foreground font-medium">That post could not be found</div>
        <p className="text-[13.5px] text-muted mt-1.5">It may have been deleted or regenerated.</p>
        <button onClick={() => navigate("/calendar")} className="mt-5 inline-flex items-center gap-1.5 text-sm text-foreground hover:opacity-80">
          <ArrowLeft size={15} /> Back to calendar
        </button>
      </div>
    );
  }

  const meta = PLATFORMS[post.platform] || { name: post.platform };
  const exists = hasContent(post.ugc);
  const dirty = JSON.stringify(brief) !== JSON.stringify({ ...emptyBrief(), ...(post.ugc || {}) });
  const set = (k, v) => setBrief((b) => ({ ...b, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await saveUgc(postId, brief);
    } catch {
      showToast("Could not save the UGC brief");
    }
    setSaving(false);
  };

  const exportFile = () => {
    const blob = new Blob([ugcToText(post, brief)], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(post.org || "remifo").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${post.platform}-ugc.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = (fmt) => {
    if (fmt === "md") {
      exportFile();
      return;
    }
    exportItem(postId, "ugc", fmt);
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(ugcToText(post, brief));
      showToast("UGC brief copied");
    } catch {
      showToast("Copy failed, select the text manually");
    }
  };

  return (
    <div className="animate-fade-in max-w-3xl">
      <button onClick={() => navigate("/calendar")} className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-foreground transition mb-4">
        <ArrowLeft size={15} /> Back to calendar
      </button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <PlatformIcon id={post.platform} size={15} className="text-foreground" />
            <h1 className="text-[24px] sm:text-[28px] font-bold tracking-tight text-foreground" style={{ letterSpacing: "-0.02em" }}>
              UGC brief
            </h1>
          </div>
          <p className="text-muted text-[13.5px]">
            {meta.name} · {fmtDateLong(post.date)} · {fmtTime(post.time)}
          </p>
        </div>
        {exists ? (
          <div className="flex gap-2 w-full sm:w-auto [&>*]:flex-1 sm:[&>*]:flex-none">
            <GhostBtn onClick={() => generateUgc(postId)}>
              {busy ? <Spinner size={15} /> : <RefreshCw size={15} />}
              Regenerate
            </GhostBtn>
            <GhostBtn onClick={copyAll}>
              <Copy size={15} />
              Copy
            </GhostBtn>
            <ExportMenu
              full
              options={[
                { key: "md", label: "Markdown (.md)" },
                { key: "pdf", label: "PDF" },
                { key: "docx", label: "Word (.docx)" },
              ]}
              onPick={handleExport}
            />
            <PrimaryBtn onClick={save} disabled={!dirty || saving}>
              <Save size={16} />
              Save
            </PrimaryBtn>
          </div>
        ) : null}
      </div>

      {post.body ? (
        <div className="bg-surface border border-border rounded-2xl p-4 mb-5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-faint mb-1.5">From this post</div>
          <p className="text-[13.5px] text-muted whitespace-pre-wrap leading-relaxed line-clamp-4">{post.body}</p>
        </div>
      ) : null}

      {!exists ? (
        <div className="border border-dashed border-border rounded-2xl p-10 text-center bg-surface">
          <div className="w-12 h-12 rounded-2xl bg-foreground/[0.06] flex items-center justify-center mx-auto mb-4">
            <Users size={22} className="text-muted" />
          </div>
          <div className="text-foreground font-medium text-[15px]">No UGC brief yet</div>
          <p className="text-[13.5px] text-muted mt-1.5 max-w-sm mx-auto">
            Generate an authentic, creator style brief for this post: concept, hook, a spoken script, and a shot list, grounded in your knowledge base.
          </p>
          <button
            onClick={() => generateUgc(postId)}
            disabled={busy}
            className="mt-5 inline-flex items-center gap-2 bg-accent text-accent-foreground rounded-xl px-5 py-2.5 text-sm font-semibold transition hover:opacity-90 active:scale-[0.97] disabled:opacity-60"
          >
            {busy ? <Spinner size={16} /> : null}
            {busy ? "Generating" : "Generate UGC brief"}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-surface border border-border rounded-2xl p-4 space-y-4">
            <Field label="Concept">
              <textarea className={`${INPUT} text-[14px] leading-relaxed min-h-[64px]`} value={brief.concept} placeholder="The UGC idea and angle" onChange={(e) => set("concept", e.target.value)} />
            </Field>
            <Field label="Format">
              <input className={`${INPUT} text-[14px]`} value={brief.format} placeholder="e.g. Talking head selfie video" onChange={(e) => set("format", e.target.value)} />
            </Field>
            <Field label="Hook">
              <textarea className={`${INPUT} text-[14px] leading-relaxed min-h-[56px]`} value={brief.hook} placeholder="The first line or on screen text" onChange={(e) => set("hook", e.target.value)} />
            </Field>
          </div>

          <div className="bg-surface border border-border rounded-2xl p-4">
            <ListEditor
              label="Script"
              addLabel="Add line"
              items={brief.script}
              placeholder="A spoken line or talking point"
              onChange={(v) => set("script", v)}
            />
          </div>

          <div className="bg-surface border border-border rounded-2xl p-4">
            <ListEditor
              label="Shot list"
              addLabel="Add shot"
              items={brief.shots}
              placeholder="A clip to film, e.g. Close up of hands opening the app"
              onChange={(v) => set("shots", v)}
            />
          </div>

          <div className="bg-surface border border-border rounded-2xl p-4">
            <Field label="Caption">
              <textarea className={`${INPUT} text-[14px] leading-relaxed min-h-[56px]`} value={brief.caption} placeholder="Suggested caption to post with it" onChange={(e) => set("caption", e.target.value)} />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}